import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Search, Users, ArrowLeftRight, MapPin, Navigation } from "lucide-react";
import { format } from "date-fns";
import { useLanguage } from "@/context/LanguageContext";

export const SearchWidget = ({ variant = "ride", initial = {}, className = "" }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [from, setFrom] = useState(initial.from || "");
  const [to, setTo] = useState(initial.to || "");
  const [date, setDate] = useState(initial.date ? new Date(initial.date) : undefined);
  const [seats, setSeats] = useState(initial.seats || "1");

  const submit = (e) => {
    e && e.preventDefault();
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (date) params.set("date", format(date, "yyyy-MM-dd"));
    if (seats) params.set("seats", seats);
    navigate(`${variant === "bus" ? "/bus" : "/search"}?${params.toString()}`);
  };

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <form onSubmit={submit} className={`glass rounded-[var(--radius)] p-4 sm:p-5 ${className}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
        <div className="lg:col-span-3">
          <label className="text-xs uppercase tracking-wide text-muted-cosmic mb-1 block">{t("sw.from")}</label>
          <CityAutocomplete testId="ride-search-from-input" value={from} placeholder={t("sw.leavingFrom")} icon={<Navigation size={16} />} onSelect={(name) => setFrom(name)} onChangeText={setFrom} />
        </div>
        <div className="hidden lg:flex lg:col-span-1 justify-center pb-1">
          <Button type="button" variant="ghost" size="icon" onClick={swap} aria-label="Swap cities" className="text-[#22D3EE] hover:bg-white/6"><ArrowLeftRight size={16} /></Button>
        </div>
        <div className="lg:col-span-3">
          <label className="text-xs uppercase tracking-wide text-muted-cosmic mb-1 block">{t("sw.to")}</label>
          <CityAutocomplete testId="ride-search-to-input" value={to} placeholder={t("sw.goingTo")} icon={<MapPin size={16} />} onSelect={(name) => setTo(name)} onChangeText={setTo} />
        </div>
        <div className="lg:col-span-2">
          <label className="text-xs uppercase tracking-wide text-muted-cosmic mb-1 block">{t("sw.date")}</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" data-testid="ride-search-date-button" variant="outline" className="w-full h-11 justify-start bg-white/5 border-white/10 text-white hover:bg-white/8 font-normal">
                <CalendarIcon size={16} className="mr-2 text-[#22D3EE]" />
                {date ? format(date, "dd MMM") : <span className="text-white/40">{t("sw.anyDate")}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 glass-solid border-white/10" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="text-white" disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
            </PopoverContent>
          </Popover>
        </div>
        <div className="lg:col-span-1">
          <label className="text-xs uppercase tracking-wide text-muted-cosmic mb-1 block">{t("sw.seats")}</label>
          <Select value={seats} onValueChange={setSeats}>
            <SelectTrigger data-testid="ride-search-passengers-select" className="h-11 bg-white/5 border-white/10 text-white"><Users size={15} className="text-[#22D3EE]" /><SelectValue /></SelectTrigger>
            <SelectContent className="glass-solid text-white">
              {[1,2,3,4].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-2">
          <Button type="submit" data-testid="ride-search-submit-button" className="w-full h-11 bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan gap-2">
            <Search size={16} /> {t("sw.search")}
          </Button>
        </div>
      </div>
    </form>
  );
};
